/**
 * Masayı ilk açan garson takibi (sadece localStorage — DB ile alakası yok).
 *
 * Amaç: Adisyon fişinde "giriş yapan garsonun" değil, "masayı ilk açan garsonun"
 *       adının yazdırılmasını sağlamak. Bu özellik restoran ekran ayarları
 *       toolbar'ında bir toggle ile açılır/kapanır.
 *
 * Saklama formatı (per setupId):
 *   Toggle:    `restaurant_use_table_opener_for_receipt_<setupId>` → 'true'|'false'
 *   Mapping:   `restaurant_table_openers_<setupId>` → JSON:
 *              { "<tableId>": { openerName: string, openedAtIso: string } }
 *
 * Invalidation:
 *   Aynı masa kapanıp tekrar açılırsa `OpenedAt` değişir. Bu nedenle her kayıt
 *   `openedAtIso` ile saklanır; okuma sırasında mevcut activeSale'in OpenedAt'i
 *   ile eşleşmiyorsa kayıt stale kabul edilir.
 */

export interface TableOpenerEntry {
  openerName: string;
  openedAtIso: string;
}

type OpenersMap = Record<string, TableOpenerEntry>;

const TOGGLE_KEY_PREFIX = 'restaurant_use_table_opener_for_receipt_';
const MAP_KEY_PREFIX = 'restaurant_table_openers_';

const toggleKey = (setupId: string | number | null | undefined): string =>
  `${TOGGLE_KEY_PREFIX}${setupId ?? 'default'}`;

const mapKey = (setupId: string | number | null | undefined): string =>
  `${MAP_KEY_PREFIX}${setupId ?? 'default'}`;

const safeReadMap = (setupId: string | number | null | undefined): OpenersMap => {
  try {
    const raw = localStorage.getItem(mapKey(setupId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return parsed as OpenersMap;
    }
    return {};
  } catch {
    return {};
  }
};

const safeWriteMap = (setupId: string | number | null | undefined, map: OpenersMap): void => {
  try {
    localStorage.setItem(mapKey(setupId), JSON.stringify(map));
  } catch (err) {
    console.warn('[tableOpenersStorage] localStorage yazma hatası:', err);
  }
};

// ─────────────────────────────────────────────────────────────────
// TOGGLE (Masa Düzeni Ayarları'ndaki switch için)
// ─────────────────────────────────────────────────────────────────

export const getUseTableOpenerForReceipt = (setupId: string | number | null | undefined): boolean => {
  try {
    return localStorage.getItem(toggleKey(setupId)) === 'true';
  } catch {
    return false;
  }
};

export const setUseTableOpenerForReceipt = (
  setupId: string | number | null | undefined,
  value: boolean
): void => {
  try {
    localStorage.setItem(toggleKey(setupId), value ? 'true' : 'false');
  } catch (err) {
    console.warn('[tableOpenersStorage] toggle yazma hatası:', err);
  }
};

// ─────────────────────────────────────────────────────────────────
// MAPPING (tableId → openerName)
// ─────────────────────────────────────────────────────────────────

/**
 * Açan garson kaydını yazar (idempotent).
 *  - Eğer aynı tableId için kayıt zaten var ve openedAtIso eşleşiyorsa: dokunmaz.
 *  - Eşleşmiyorsa veya hiç yoksa: yeni kayıt yazar.
 *
 * Bu sayede aynı masaya bir başka garson PIN ile girince openerName değişmez,
 * ama masa kapanıp gerçekten yeniden açılınca (OpenedAt değişince) kayıt yenilenir.
 */
export const recordTableOpener = (
  setupId: string | number | null | undefined,
  tableId: string | number,
  openerName: string,
  openedAtIso: string
): void => {
  if (!tableId || !openerName || !openedAtIso) return;

  const map = safeReadMap(setupId);
  const key = String(tableId);
  const existing = map[key];

  if (existing && existing.openedAtIso === openedAtIso && existing.openerName === openerName) {
    return; // No-op
  }

  if (existing && existing.openedAtIso === openedAtIso) {
    return; // Same session — preserve original opener even if a different waiter logged in
  }

  map[key] = { openerName, openedAtIso };
  safeWriteMap(setupId, map);
};

/**
 * Bir masa için açan garsonun adını döndürür. Stale kayıtlar (openedAtIso uyuşmayan)
 * için null döndürür. Toggle KAPALI ise de null döndürür (kullanıcının opt-out hakkı).
 */
export const getTableOpenerName = (
  setupId: string | number | null | undefined,
  tableId: string | number,
  currentOpenedAtIso: string
): string | null => {
  if (!getUseTableOpenerForReceipt(setupId)) return null;
  if (!tableId || !currentOpenedAtIso) return null;

  const map = safeReadMap(setupId);
  const entry = map[String(tableId)];

  if (!entry) return null;
  if (entry.openedAtIso !== currentOpenedAtIso) return null; // stale
  return entry.openerName || null;
};

/**
 * Tek bir masanın kaydını siler (masa kapanınca / ödeme sonrası).
 */
export const clearTableOpener = (
  setupId: string | number | null | undefined,
  tableId: string | number
): void => {
  if (!tableId) return;
  const map = safeReadMap(setupId);
  if (!(String(tableId) in map)) return;
  delete map[String(tableId)];
  safeWriteMap(setupId, map);
};

/**
 * ✅ PRIMARY KAYNAK: `WaiterActionsJson`'daki ilk action'ın garsonunu döndürür.
 *
 * Backend, masa açılırken `WaiterActionsJson`'a otomatik bir şey yazmaz; ilk action
 * tipik olarak frontend'in `add_item` çağrısıdır. Yani array'in ilk elemanındaki
 * `waiter` (yeni format) veya `waiterName` (eski format) alanı = masayı ilk açan
 * (eylem yapan) garsondur. Bu DB'de kalıcıdır, tüm cihazlardan tutarlıdır ve
 * "ayar sonradan açıldı" senaryosunda dahi geriye dönük çalışır.
 *
 * Hiç action yoksa veya parse edilemezse null döner — caller fallback'e geçer.
 */
export const extractFirstOpenerFromActions = (
  waiterActionsJson: string | null | undefined
): string | null => {
  if (!waiterActionsJson) return null;
  try {
    const actions = JSON.parse(waiterActionsJson);
    if (!Array.isArray(actions) || actions.length === 0) return null;
    const first = actions[0] || {};
    const name: unknown = first.waiter ?? first.waiterName;
    if (typeof name === 'string' && name.trim() !== '' && name !== 'Bilinmeyen') {
      return name;
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Aktif satışlar listesine bakarak DB'de artık olmayan tüm openers kayıtlarını siler.
 * RestaurantScreen.tsx'in `loadActiveSales` sonrasında çağrılır.
 */
export const pruneStaleOpeners = (
  setupId: string | number | null | undefined,
  activeTableIds: Array<string | number>
): void => {
  const map = safeReadMap(setupId);
  const validKeys = new Set(activeTableIds.map(String));
  let changed = false;

  for (const key of Object.keys(map)) {
    if (!validKeys.has(key)) {
      delete map[key];
      changed = true;
    }
  }

  if (changed) safeWriteMap(setupId, map);
};
